"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergeFilters = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var objRef_1 = require("../../objRef");
var index_1 = require("./index");
var compact_1 = tslib_1.__importDefault(require("lodash/compact"));
var groupBy_1 = tslib_1.__importDefault(require("lodash/groupBy"));
var last_1 = tslib_1.__importDefault(require("lodash/last"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var values_1 = tslib_1.__importDefault(require("lodash/values"));
function separateFiltersByType(filters) {
    var result = {
        attribute: [],
        date: [],
        measureValue: [],
        ranking: [],
    };
    filters.forEach(function (f) {
        if (index_1.isAttributeFilter(f)) {
            result.attribute.push(f);
        }
        else if (index_1.isDateFilter(f)) {
            result.date.push(f);
        }
        else if (index_1.isMeasureValueFilter(f)) {
            result.measureValue.push(f);
        }
        else if (index_1.isRankingFilter(f)) {
            result.ranking.push(f);
        }
        ts_invariant_1.default(f, "filter is not supported");
    });
    return result;
}
/**
 * Merges two sets of filters.
 *
 * - Attribute filters and ranking filters from both sets are simply concatenated resulting
 *   in the filters being ANDed together.
 * - Date filters are merged based on date data set they filter on
 *   - For Date filters for the same date data set:
 *     - the filters are ordered putting original filters first
 *     - the last filter in this ordering is taken
 *        - if it is All time, all filters for the dimension are cleared
 *        - else the last filter is used
 * - Measure value filters are merged so that there is at most one Measure value filter per measure
 *   (the last one specified is used). This is to prevent errors with more than one Measure value filter
 *   on the same measure which is not supported.
 *
 * @remarks
 * It is the responsibility of the caller to make sure all the filters use the same ObjRef type so that
 * they can be compared without involving the backend. Otherwise, the results might be unexpected
 * (especially for date filters).
 *
 * There is also a function in backend insights service called getInsightWithAddedFilters that can help you
 * do this that takes care of the ObjRef normalization.
 *
 * @param originalFilters - original filters to merge with
 * @param addedFilters - new filters to add on top of original
 * @internal
 */
function mergeFilters(originalFilters, addedFilters) {
    ts_invariant_1.default(originalFilters, "original filters must be specified");
    var filtersToMerge = compact_1.default(addedFilters !== null && addedFilters !== void 0 ? addedFilters : []);
    if (!filtersToMerge.length) {
        return originalFilters;
    }
    if (!originalFilters.length) {
        return filtersToMerge.filter(function (f) { return !index_1.isAllTimeDateFilter(f); });
    }
    var original = separateFiltersByType(originalFilters);
    var added = separateFiltersByType(filtersToMerge);
    // concat attribute filters
    var attributeFilters = tslib_1.__spreadArrays(original.attribute, added.attribute);
    // merge date filters by date dataset qualifier
    var dateFilters = mergeDateFilters(original.date, added.date);
    // merge measure value filters by measure
    var measureValueFilters = mergeMeasureValueFilters(original.measureValue, added.measureValue);
    // concat ranking filters
    var rankingFilters = tslib_1.__spreadArrays(original.ranking, added.ranking);
    return tslib_1.__spreadArrays(attributeFilters, dateFilters, measureValueFilters, rankingFilters);
}
exports.mergeFilters = mergeFilters;
function mergeDateFilters(originalFilters, addedFilters) {
    var allFilters = tslib_1.__spreadArrays(originalFilters, addedFilters);
    var grouped = groupBy_1.default(allFilters, function (f) { return objRef_1.objRefToString(index_1.filterObjRef(f)); });
    var mergedFilters = [];
    values_1.default(grouped).forEach(function (filtersForDimension) {
        // use the last filter for the dimension specified.
        // this makes sure that the added filter wins if it is specified
        var lastFilterForDimension = last_1.default(filtersForDimension);
        // if the last filter is all time, clear filters for this dimension, otherwise use the last filter
        if (!index_1.isAllTimeDateFilter(lastFilterForDimension)) {
            mergedFilters.push(lastFilterForDimension);
        }
    });
    return mergedFilters;
}
function mergeMeasureValueFilters(originalFilters, addedFilters) {
    var allFilters = tslib_1.__spreadArrays(originalFilters, addedFilters);
    var grouped = groupBy_1.default(allFilters, function (f) { return objRef_1.objRefToString(f.measureValueFilter.measure); });
    return values_1.default(grouped).map(function (filters) { return last_1.default(filters); });
}
//# sourceMappingURL=filterMerge.js.map